import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { isMobileViewport, useIsMobile } from '@/hooks/use-mobile';
import { BottomSheetTextButton, SegmentedControl } from '@/components/mobile';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { toast } from 'sonner';

import type { RootState } from '@/reducers';
import {
	useGetCustomerRemindersQuery,
	useCreateReminderMutation,
	useUpdateReminderMutation,
	useCompleteReminderMutation,
	useRescheduleReminderMutation,
	useDeleteReminderMutation,
	type Reminder,
} from '@/slices/remindersApi';
import { useGetEnrollmentsQuery } from '@/slices/reminderSequencesApi';
import ReminderRow from '@/components/reminders/ReminderRow';
import AddToReminderSequence from '@/components/reminder-sequences/AddToReminderSequence';
import CustomerReminderSequences from '@/components/reminder-sequences/CustomerReminderSequences';
import ReminderComposer, {
	type ReminderComposerSubmit,
	type ReminderComposerValue,
} from '@/components/reminders/ReminderComposer';
import {
	getDefaultNotificationOffset,
	getReminderAssignees,
	keepNotificationDefault,
} from '@/components/reminders/reminderHelpers';
import {
	changedNotifications,
	newReminderNotifications,
	readNotifications,
	sameNotifications,
	singleOffset,
} from '@/components/notifications/notificationSettings';
import {
	DetailsSectionActions,
	DetailsSectionHeader,
	detailsChevronClass,
} from './DetailsSection';
import {
	captureReminderDraft,
	captureReminderNotifyOpen,
	readReminderDraft,
	writeReminderDraft,
} from './reminderDrafts';
// Phones: adding and editing open as full-screen forms with Cancel in the
// header and Save pinned above the keyboard (as on the Reminders tab).
import {
	PHONE_DIALOG_CLASS,
	PHONE_DIALOG_HEADER_CLASS,
	PHONE_DIALOG_TITLE_CLASS,
} from '../../teams/phoneDialog';

// This customer's reminders: one-off reminders, then any reminder sequences
// they are on. + adds either.

// Whether a new reminder's form holds anything beyond what a fresh one starts with.
const isStartedNewReminder = (value?: ReminderComposerValue) =>
	Boolean(
		value &&
			(value.text ||
				value.notes ||
				value.dateTime ||
				value.priority !== 'none' ||
				value.recurrence !== 'none' ||
				value._assignees.length ||
				value.customer ||
				!sameNotifications(
					value.notifications,
					newReminderNotifications(getDefaultNotificationOffset())
				))
	);

type AddMode = 'reminder' | 'sequence';

interface RemindersProps {
	_customer?: { _id: string; name?: string } | null;
}

const Reminders = ({ _customer }: RemindersProps) => {
	const customerId = _customer?._id;
	const users = useSelector((state: RootState) => state.users || []);
	const isMobile = useIsMobile();
	const maskPhone = usePhoneMask();
	const firstName = maskPhone(_customer?.name?.split(' ')[0] || 'this customer');

	const [isOpen, setIsOpen] = usePersistentOpen(
		'conversation-details:reminders'
	);
	const draftKey = customerId;
	const [restoredDraft] = useState(() => readReminderDraft(draftKey));
	const [showAddForm, setShowAddForm] = useState(restoredDraft?.mode === 'new');
	// Not remembered: + always starts on Reminder here.
	const [addMode, setAddMode] = useState<AddMode>('reminder');
	const [editingReminder, setEditingReminder] = useState<Reminder | null>(
		null
	);
	// an edit form to reopen once the reminders have loaded
	const pendingEditId = useRef(
		restoredDraft?.mode === 'edit' ? restoredDraft.id : undefined
	);

	// Loaded while closed too, for the count in the header.
	const {
		data,
		isLoading: remindersLoading,
		isFetching: remindersFetching,
	} = useGetCustomerRemindersQuery(
		{ _customer: customerId as string, kind: 'oneoff' },
		{ skip: !customerId }
	);
	// The same arguments as CustomerReminderSequences, so both share one request.
	const {
		data: enrollmentsData,
		isLoading: enrollmentsLoading,
		isFetching: enrollmentsFetching,
	} = useGetEnrollmentsQuery({ _customer: customerId }, { skip: !customerId });

	const isLoading = remindersLoading || enrollmentsLoading;
	const isFetching = remindersFetching || enrollmentsFetching;

	const reminders = data?.reminders ?? [];
	const runs = enrollmentsData?.enrollments ?? [];
	const activeRuns = runs.filter((run) => run.status === 'active');
	const openOneOffs = reminders.filter((reminder) => !reminder.isCompleted);
	// Each active sequence counts once, however many steps it has.
	const count = openOneOffs.length + activeRuns.length;

	useEffect(() => {
		const id = pendingEditId.current;
		if (!id || !data) return;
		const reminder = data.reminders.find((r) => r._id === id);
		// Still pending until the form shows (the effect below clears it), so
		// cached reminders don't let that effect drop the draft first.
		if (reminder) setEditingReminder(reminder);
		else {
			pendingEditId.current = undefined;
			writeReminderDraft(draftKey, null);
		}
	}, [data, draftKey]);

	// Keep the draft in step with the open form; closing it drops the draft.
	useEffect(() => {
		const draft = readReminderDraft(draftKey);
		if (showAddForm) {
			if (draft?.mode !== 'new') {
				writeReminderDraft(draftKey, { mode: 'new' });
			}
		} else if (editingReminder) {
			pendingEditId.current = undefined;
			if (draft?.mode !== 'edit' || draft.id !== editingReminder._id) {
				writeReminderDraft(draftKey, { mode: 'edit', id: editingReminder._id });
			}
		} else if (!pendingEditId.current) {
			writeReminderDraft(draftKey, null);
		}
	}, [showAddForm, editingReminder, draftKey]);

	// Leaving the details (rather than the layout swapping) drops the draft.
	const isMobileRef = useRef(isMobile);
	isMobileRef.current = isMobile;
	useEffect(
		() => () => {
			if (isMobileViewport() === isMobileRef.current) {
				writeReminderDraft(draftKey, null);
			}
		},
		[draftKey]
	);

	// What a reopened form starts with (read when it mounts).
	const savedDraft = readReminderDraft(draftKey);
	const newReminderInitial =
		savedDraft?.mode === 'new' && isStartedNewReminder(savedDraft.value)
			? savedDraft.value
			: undefined;
	const editDraftValue =
		savedDraft?.mode === 'edit' && savedDraft.id === editingReminder?._id
			? savedDraft.value
			: undefined;

	// A form reads this as it mounts, so only one coming back after a swap reopens
	const notifyOpen = Boolean(savedDraft?.notifyOpen);
	const captureNotifyOpen = (open: boolean) =>
		captureReminderNotifyOpen(draftKey, open);

	const captureNewReminder = (value: ReminderComposerValue) =>
		captureReminderDraft(draftKey, { mode: 'new', value });
	const captureEditedReminder = (value: ReminderComposerValue) => {
		if (editingReminder) {
			captureReminderDraft(draftKey, { mode: 'edit', id: editingReminder._id, value });
		}
	};

	const [createReminder, { isLoading: isCreating }] =
		useCreateReminderMutation();
	const [updateReminder, { isLoading: isUpdating }] =
		useUpdateReminderMutation();
	const [completeReminder] = useCompleteReminderMutation();
	const [rescheduleReminder] = useRescheduleReminderMutation();
	const [deleteReminder] = useDeleteReminderMutation();

	const handleCreate = async (value: ReminderComposerSubmit) => {
		if (!customerId) return;
		// ReminderComposer makes a changed timing the device's default, but a form
		// brought back from a draft starts from the changed value. Comparing with
		// the real starting point here keeps that rule (a no-op otherwise).
		keepNotificationDefault(value.notifications, getDefaultNotificationOffset());
		try {
			await createReminder({ ...value, _customer: customerId }).unwrap();
			toast.success('Reminder created');
			setShowAddForm(false);
			setIsOpen(true);
		} catch {
			toast.error('Failed to create reminder');
		}
	};

	const handleEditSubmit = async (value: ReminderComposerSubmit) => {
		if (!editingReminder) return;
		keepNotificationDefault(
			value.notifications,
			singleOffset(readNotifications(editingReminder, 'reminder').offsets)
		);
		try {
			await updateReminder({
				_id: editingReminder._id,
				updates: {
					text: value.text,
					dateTime: value.dateTime,
					notes: value.notes,
					priority: value.priority,
					recurrence: value.recurrence,
					notifications: changedNotifications(
						editingReminder,
						'reminder',
						value.notifications
					),
					_assignees: value._assignees,
				},
			}).unwrap();
			toast.success('Reminder updated');
			setEditingReminder(null);
		} catch {
			toast.error('Failed to update reminder');
		}
	};

	const handleToggleComplete = async (reminder: Reminder) => {
		try {
			const res = await completeReminder({
				_id: reminder._id,
				isCompleted: !reminder.isCompleted,
			}).unwrap();
			if (res.nextReminder) {
				toast.success('Completed. Next occurrence scheduled.');
			}
		} catch {
			toast.error('Failed to update reminder');
		}
	};

	const handleSnooze = async (reminder: Reminder, isoDate: string) => {
		try {
			await rescheduleReminder({
				_id: reminder._id,
				dateTime: isoDate,
			}).unwrap();
			toast.success('Reminder snoozed');
		} catch {
			toast.error('Failed to snooze reminder');
		}
	};

	const handleDelete = async (reminder: Reminder) => {
		try {
			await deleteReminder(reminder._id).unwrap();
			toast.success('Reminder deleted');
		} catch {
			toast.error('Failed to delete reminder');
		}
	};

	return (
		<div className="border-b border-border/60">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<DetailsSectionHeader
					className="flex-row space-y-0 shrink-0"
					title={count ? `Reminders (${count})` : 'Reminders'}
					icon={
						<svg aria-hidden="true" viewBox="0 0 24 24" className={detailsChevronClass}>
							<path
								d="M7 10l5 5 5-5"
								stroke="currentColor"
								strokeWidth="2"
								fill="none"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					}
					actions={
						<DetailsSectionActions>
							<div className="flex items-center space-x-1">
								{isFetching && !isLoading && (
									<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
								)}
								<Button
									variant="ghost"
									size="icon"
									onClick={() => {
										setAddMode('reminder');
										setShowAddForm(true);
										setIsOpen(true);
									}}
									className="h-7 w-7 max-md:size-11 max-md:rounded-full"
									disabled={!customerId}
									aria-label="Add reminder or sequence"
								>
									<Plus className="h-4 w-4 max-md:size-5" />
								</Button>
							</div>
						</DetailsSectionActions>
					}
				/>

				<CollapsibleContent>
					<div className="overflow-y-auto">
						{isLoading ? (
							<div className="flex justify-center items-center h-24">
								<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
							</div>
						) : (
							<>
								{reminders.length === 0 && activeRuns.length === 0 && (
									<div className="px-4 py-4 pt-1">
										<p className="text-sm font-medium text-muted-foreground">
											No reminders yet
										</p>
										<p className="text-xs text-muted-foreground/80 mt-0.5">
											<span className="max-md:hidden">Click</span>
											<span className="md:hidden">Tap</span> the &apos;+&apos; to add
											a reminder or put {firstName} on a sequence.
										</p>
									</div>
								)}
								{reminders.length > 0 && (
									<div className="divide-y divide-border">
										{reminders.map((reminder) => (
											<ReminderRow
												key={reminder._id}
												reminder={reminder}
												showCustomer={false}
												onToggleComplete={handleToggleComplete}
												onEdit={setEditingReminder}
												onSnooze={handleSnooze}
												onDelete={handleDelete}
											/>
										))}
									</div>
								)}
								{/* Not mounted without runs: its padding would leave a gap. */}
								{customerId && runs.length > 0 && (
									<div className="border-t border-border/60">
										<p className="px-4 pt-3 pb-2 text-xs font-medium text-muted-foreground">
											Sequences
										</p>
										<CustomerReminderSequences
											customerId={customerId}
											customerName={_customer?.name}
										/>
									</div>
								)}
							</>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>

			<Dialog open={showAddForm} onOpenChange={setShowAddForm}>
				<DialogContent
					mobileVariant="fullscreen"
					// phones have Cancel in the header
					hideClose="mobile"
					className={isMobile ? PHONE_DIALOG_CLASS : 'sm:max-w-lg'}
				>
					<DialogHeader className={isMobile ? PHONE_DIALOG_HEADER_CLASS : undefined}>
						{isMobile && (
							<BottomSheetTextButton
								className="justify-self-start"
								onClick={() => setShowAddForm(false)}
							>
								Cancel
							</BottomSheetTextButton>
						)}
						<DialogTitle className={isMobile ? PHONE_DIALOG_TITLE_CLASS : undefined}>
							New reminder
						</DialogTitle>
						{isMobile && <span aria-hidden />}
					</DialogHeader>
					<div className="max-md:shrink-0 max-md:px-4 max-md:pt-3">
						<SegmentedControl<AddMode>
							aria-label="Reminder or sequence"
							value={addMode}
							onValueChange={setAddMode}
							options={[
								{ value: 'reminder', label: 'Reminder' },
								{ value: 'sequence', label: 'Sequence' },
							]}
							className="w-full"
						/>
					</div>
					{addMode === 'reminder' ? (
						// Mounts again after a switch to Sequence, starting from the
						// latest draft (read each render), so typed text stays.
						<ReminderComposer
							users={users}
							stickyActions={isMobile}
							initial={newReminderInitial}
							saving={isCreating}
							submitLabel="Add reminder"
							onSubmit={handleCreate}
							onCancel={() => setShowAddForm(false)}
							onChange={captureNewReminder}
							notifyOpen={notifyOpen}
							onNotifyOpenChange={captureNotifyOpen}
						/>
					) : (
						customerId && (
							<AddToReminderSequence
								customer={{ _id: customerId, name: _customer?.name }}
								source="conversation"
								stickyActions={isMobile}
								onAdded={() => {
									setShowAddForm(false);
									setIsOpen(true);
								}}
								onCancel={() => setShowAddForm(false)}
							/>
						)
					)}
				</DialogContent>
			</Dialog>

			<Dialog
				open={Boolean(editingReminder)}
				onOpenChange={(open) => !open && setEditingReminder(null)}
			>
				<DialogContent
					mobileVariant="fullscreen"
					// phones have Cancel in the header
					hideClose="mobile"
					className={isMobile ? PHONE_DIALOG_CLASS : undefined}
				>
					<DialogHeader className={isMobile ? PHONE_DIALOG_HEADER_CLASS : undefined}>
						{isMobile && (
							<BottomSheetTextButton
								className="justify-self-start"
								onClick={() => setEditingReminder(null)}
							>
								Cancel
							</BottomSheetTextButton>
						)}
						<DialogTitle className={isMobile ? PHONE_DIALOG_TITLE_CLASS : undefined}>
							Edit reminder
						</DialogTitle>
						{isMobile && <span aria-hidden />}
					</DialogHeader>
					{editingReminder && (
						<ReminderComposer
							users={users}
							stickyActions={isMobile}
							saving={isUpdating}
							submitLabel="Save changes"
							creatorId={editingReminder._user}
							initial={
								editDraftValue ?? {
									text: editingReminder.text,
									dateTime: editingReminder.dateTime,
									notes: editingReminder.notes ?? '',
									priority: editingReminder.priority,
									recurrence:
										editingReminder.recurrence?.rule ?? 'none',
									// Pass through as-is: undefined (legacy) means
									// "at time" in the composer — never `?? 0`.
									notifications: editingReminder.notifications,
									notificationOffsetMinutes:
										editingReminder.notificationOffsetMinutes,
									_assignees: getReminderAssignees(editingReminder).map(
										(user) => user._id
									),
								}
							}
							onSubmit={handleEditSubmit}
							onCancel={() => setEditingReminder(null)}
							onChange={captureEditedReminder}
							notifyOpen={notifyOpen}
							onNotifyOpenChange={captureNotifyOpen}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default Reminders;
